type PublicEnv = Partial<
  Record<
    'NEXT_PUBLIC_ENV' | 'ENV' | 'NODE_ENV' | 'NEXT_PUBLIC_POSTHOG_ENABLED',
    string | undefined
  >
>

// Keep direct property reads so Next can inline client-side values.
// Passing the whole process.env object falls back to the browser process polyfill.
const getDefaultPublicEnv = (): PublicEnv => ({
  NEXT_PUBLIC_ENV: process.env.NEXT_PUBLIC_ENV,
  ENV: process.env.ENV,
  NODE_ENV: process.env.NODE_ENV,
  NEXT_PUBLIC_POSTHOG_ENABLED: process.env.NEXT_PUBLIC_POSTHOG_ENABLED,
})

const TRUE_VALUES = new Set(['1', 'true', 'yes', 'on'])
const FALSE_VALUES = new Set(['0', 'false', 'no', 'off'])
const DEFAULT_ANALYTICS_STAGES = new Set(['production', 'staging', 'preview'])

function readBooleanEnv(
  rawValue: string | undefined,
  fallback: boolean,
): boolean {
  if (!rawValue) return fallback

  const normalized = rawValue.trim().toLowerCase()
  if (TRUE_VALUES.has(normalized)) return true
  if (FALSE_VALUES.has(normalized)) return false

  return fallback
}

function readReleaseStage(env: PublicEnv): string {
  const rawStage = [env.NEXT_PUBLIC_ENV, env.ENV, env.NODE_ENV].find(
    (value) => value && value.trim().length > 0,
  )

  return rawStage?.trim().toLowerCase() ?? 'development'
}

export function resolvePostHogRuntimeOptions(
  env: PublicEnv = getDefaultPublicEnv(),
) {
  const releaseStage = readReleaseStage(env)
  const defaultAnalyticsEnabled = DEFAULT_ANALYTICS_STAGES.has(releaseStage)

  const analyticsEnabled = readBooleanEnv(
    env.NEXT_PUBLIC_POSTHOG_ENABLED,
    defaultAnalyticsEnabled,
  )

  return {
    releaseStage,
    analyticsEnabled,
  }
}
